import { ArrowLeft, Shield, Lock, Server, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const DataProtection = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-crystal-charcoal via-crystal-charcoal/95 to-crystal-charcoal">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 mb-6" asChild>
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
          <h1 className="text-4xl font-bold text-white mb-4">Data Protection & Security</h1>
          <p className="text-white/60">Our commitment to protecting your data and ensuring compliance</p>
        </div>

        <div className="space-y-8">
          {/* Overview */}
          <div className="glass-panel p-8">
            <div className="flex items-center mb-4">
              <Shield className="h-8 w-8 text-crystal-orange mr-3" />
              <h2 className="text-2xl font-semibold text-white">Security Overview</h2>
            </div>
            <p className="text-white/80 leading-relaxed">
              Crystal HACCP implements enterprise-grade security measures to protect your food safety data. 
              Our multi-layered approach ensures data integrity, confidentiality, and availability across 
              all platforms and regions.
            </p>
          </div>

          {/* Data Residency */}
          <div className="glass-panel p-8">
            <div className="flex items-center mb-6">
              <Server className="h-8 w-8 text-crystal-orange mr-3" />
              <h2 className="text-2xl font-semibold text-white">Data Residency & Localization</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-white/10 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white mb-3">EU/EEA Regions</h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li>• Data stored in EU-based data centers</li>
                  <li>• GDPR compliant processing</li>
                  <li>• Data transfers within EU/EEA only</li>
                  <li>• Right to data portability</li>
                </ul>
              </div>
              
              <div className="border border-white/10 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white mb-3">Norway Specific</h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li>• Compliance with Personvernloven</li>
                  <li>• Local data processing requirements</li>
                  <li>• Mattilsynet regulation alignment</li>
                  <li>• Norwegian language support</li>
                </ul>
              </div>
              
              <div className="border border-white/10 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white mb-3">UK Regions</h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li>• UK GDPR compliance</li>
                  <li>• Data stored in UK data centers</li>
                  <li>• FSA regulation compatibility</li>
                  <li>• Adequate transfer mechanisms</li>
                </ul>
              </div>
              
              <div className="border border-white/10 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white mb-3">North America</h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li>• CCPA compliance (California)</li>
                  <li>• PIPEDA compliance (Canada)</li>
                  <li>• SOC 2 Type II certified infrastructure</li>
                  <li>• FDA/CFIA regulation support</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Technical Security */}
          <div className="glass-panel p-8">
            <div className="flex items-center mb-6">
              <Lock className="h-8 w-8 text-crystal-orange mr-3" />
              <h2 className="text-2xl font-semibold text-white">Technical Safeguards</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-lg font-medium text-white mb-3">Encryption</h3>
                <ul className="text-white/80 space-y-1 text-sm">
                  <li>• TLS 1.3 in transit</li>
                  <li>• AES-256 at rest</li>
                  <li>• End-to-end for sensitive data</li>
                  <li>• Key rotation policies</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-white mb-3">Access Controls</h3>
                <ul className="text-white/80 space-y-1 text-sm">
                  <li>• Role-based permissions</li>
                  <li>• Multi-factor authentication</li>
                  <li>• Zero-trust architecture</li>
                  <li>• Regular access reviews</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-white mb-3">Infrastructure</h3>
                <ul className="text-white/80 space-y-1 text-sm">
                  <li>• SOC 2 Type II certified</li>
                  <li>• ISO 27001 compliance</li>
                  <li>• Regular penetration testing</li>
                  <li>• 24/7 security monitoring</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Compliance Framework */}
          <div className="glass-panel p-8">
            <div className="flex items-center mb-6">
              <Eye className="h-8 w-8 text-crystal-orange mr-3" />
              <h2 className="text-2xl font-semibold text-white">Compliance & Auditing</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-white mb-3">Audit Capabilities</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-4">
                    <h4 className="font-medium text-white mb-2">Data Access Logs</h4>
                    <p className="text-white/70 text-sm">Complete audit trail of all data access and modifications</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <h4 className="font-medium text-white mb-2">Compliance Reports</h4>
                    <p className="text-white/70 text-sm">Automated generation of regulatory compliance documents</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <h4 className="font-medium text-white mb-2">Data Processing Records</h4>
                    <p className="text-white/70 text-sm">Article 30 GDPR compliance documentation</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <h4 className="font-medium text-white mb-2">Breach Notifications</h4>
                    <p className="text-white/70 text-sm">Automated incident response and notification procedures</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-white mb-3">Data Subject Rights</h3>
                <p className="text-white/80 mb-4">We provide tools to exercise your data protection rights:</p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-crystal-orange rounded-full mr-2"></div>
                      <span className="text-white/80">Right of Access (Article 15)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-crystal-orange rounded-full mr-2"></div>
                      <span className="text-white/80">Right to Rectification (Article 16)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-crystal-orange rounded-full mr-2"></div>
                      <span className="text-white/80">Right to Erasure (Article 17)</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-crystal-orange rounded-full mr-2"></div>
                      <span className="text-white/80">Right to Data Portability (Article 20)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-crystal-orange rounded-full mr-2"></div>
                      <span className="text-white/80">Right to Object (Article 21)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-crystal-orange rounded-full mr-2"></div>
                      <span className="text-white/80">Right to Restrict Processing (Article 18)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="glass-panel p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Data Protection Officer</h2>
            <p className="text-white/80 leading-relaxed mb-4">
              For any data protection inquiries, exercise of rights, or security concerns, please contact our 
              Data Protection Officer:
            </p>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white font-medium">Email: <a href="mailto:dpo@themikanaohub.com" className="text-crystal-orange hover:text-crystal-orange-glow transition-colors">dpo@themikanaohub.com</a></p>
              <p className="text-white/80 text-sm mt-2">Response time: Within 30 days (or 72 hours for urgent security matters)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProtection;